import type { Dictionary } from './types.js';

// Every term in `states`/`automationModes`/`attentionReasons`/`actions` is
// copied verbatim from the visual design spec's section 8.2 glossary tables,
// not retranslated here.
export const ja: Dictionary = {
  nav: {
    home: 'ホーム',
    calendar: 'カレンダー',
    campaigns: 'キャンペーン',
    projects: 'プロジェクト',
    activity: 'アクティビティ',
    aiConnections: 'AI接続',
    settings: '設定',
  },
  states: {
    draft: '下書き',
    validated: '検証済み',
    needsReview: '要レビュー',
    policyCheck: 'ポリシー判定',
    approved: '承認済み',
    scheduled: '予約済み',
    publishing: '公開処理中',
    published: '公開済み',
    failed: '失敗',
    canceled: '取消済み',
    held: '保留',
  },
  automationModes: {
    manual: '手動承認',
    guardedAuto: '条件付き自動',
    autonomous: '全自動',
    automationPaused: '自動化を停止中',
  },
  attentionReasons: {
    waitingForApproval: '承認待ち',
    reconnectionRequired: '再接続が必要',
    validationFailed: '仕様変更により検証エラー',
    publicationFailed: '公開に失敗',
    rejectedByPlatform: 'プラットフォームが拒否',
    creditOrRateLimit: 'クレジット切れ・レート制限',
    mediaInvalid: 'メディアが不正',
    dailyCapReached: '日次上限に到達',
    automationStopped: '自動化が停止中',
    heldOrOverdue: '保留・公開遅延',
    campaignPartlyFailed: '一部の配信先が失敗',
  },
  actions: {
    approve: '承認する',
    requestChanges: '修正を依頼',
    saveDraft: '下書きを保存',
    validate: '検証する',
    submitForReview: 'レビューに送る',
    schedule: '予約する',
    reschedule: '予約を変更',
    cancelSchedule: '予約を取り消す',
    publishNow: '今すぐ公開',
    retry: '再試行',
    reconnect: '再接続する',
    revoke: '取り消す',
    copyToProject: 'プロジェクトに複製',
    pauseAutomation: '自動化を停止',
    resumeAutomation: '自動化を再開',
  },
  ui: {
    workspaceAllProjects: 'すべてのプロジェクト',
    signedInAs: (email) => `${email} でサインイン中`,
    navNotYetAvailable: 'まだ利用できません',
    auth: {
      title: 'サインイン',
      emailLabel: 'メールアドレス',
      passwordLabel: 'パスワード',
      submit: 'サインイン',
      submitting: 'サインイン中…',
      failure: '入力内容が一致しませんでした。メールアドレスとパスワードを確認してもう一度お試しください。',
      sessionLoading: 'セッションを確認しています…',
      signOut: 'サインアウト',
    },
    home: {
      title: 'ホーム',
      subtitle: (count) => `未解決の項目が ${count} 件あります`,
      createProject: 'プロジェクトを作成',
      needsAttention: '要対応',
      unresolvedCount: (count) => `未解決 ${count} 件`,
      noAttention: '対応が必要な項目はありません。',
      projects: 'プロジェクト',
      projectName: 'プロジェクト',
      connectionsColumn: '連携',
      attentionColumn: '要対応',
      open: '開く',
      noProjects: 'プロジェクトがまだありません',
      createProjectPrompt: 'キャンペーンはすべてプロジェクトの中に作成されます。まずプロジェクトを作成してください。',
      loading: '読み込み中…',
      loadFailed: 'ホームの読み込みに失敗しました。プロジェクト一覧は引き続き利用できます。',
      retry: '再試行',
      notConnected: '未接続',
      automationColumn: '自動化',
    },
    review: {
      backToHome: '要対応一覧に戻る',
      title: 'レビュー',
      whyReview: 'レビューが必要な理由',
      whyReviewAiBody: 'このリビジョンは MCP 経由で作成・提案されました。MCP のグラントはコンテンツの作成と予約はできますが、承認はできません — 承認はこの画面での人による操作です。',
      mcpClientFallback: 'MCP クライアント',
      platformPreview: (platformLabel) => `プラットフォームプレビュー — ${platformLabel}`,
      fieldContent: 'フィールド内容',
      postText: '投稿テキスト',
      readOnlyHint: 'ここは読み取り専用です。編集するとキャンペーンエディタが開き、新しいリビジョンが作成されます。',
      attachedMedia: (count) => `添付メディア — ${count} 件`,
      pinTitle: 'タイトル',
      pinDescription: '説明',
      pinBoard: 'ボード',
      pinLink: 'リンク先',
      pinLinkNone: 'リンク先なし',
      pinImageAlt: '画像の代替テキスト',
      validation: '検証',
      validationPassed: '合格',
      validationFailed: '不合格',
      schedule: '予約',
      notScheduled: 'まだ予約されていません。',
      approveFirst: '予約する前にこのリビジョンを承認してください。',
      publishFailed: '公開に失敗しました',
      publishFailedHint: '原因を解消してから、内容を編集して再度承認・予約してください。投稿は行われていません。',
      attemptHistory: '試行履歴',
      attemptNumber: '回',
      attemptResult: '結果',
      attemptReason: '理由',
      attemptDetail: '詳細',
      attemptOk: '成功',
      attemptFailed: '失敗',
      recentActivity: '最近のアクティビティ',
      activityWhen: '日時',
      activityOrigin: '発信元',
      activityAction: '操作',
      activityResult: '結果',
      noActivity: 'まだ記録されたアクティビティはありません。',
      actions: '操作',
      reasonLabel: '修正を依頼する理由',
      reasonPlaceholder: '何をどう変更する必要があるか。',
      reasonHint: '作成者に差し戻され、アクティビティに記録されます。',
      requestChangesBlocked: '先に理由を入力してください。バリアントは下書きに戻り、予約は解除されます。',
      edit: '編集',
      editBlocked: 'キャンペーンエディタはこのフェーズには含まれていません。',
      cancelScheduleHint: '公開予定時刻を削除します。リビジョンとメディアはそのまま保持されます。',
      loading: '読み込み中…',
      loadFailed: 'このバリアントを読み込めませんでした。',
      notFound: '見つかりません。',
      approveConsequence: (local, utc) => `承認すると ${local} / ${utc} にこの投稿が公開されます。以後の確認はありません。`,
      revisionNumber: (n) => `リビジョン ${n}`,
      revisionImmutable: (n) => `リビジョン ${n} — 不変`,
      approveUnavailable: '承認は「要レビュー」で、かつ検証に合格しているリビジョンでのみ行えます。',
      requestChangesUnavailable: '現在の状態では修正の依頼はできません。',
      publishedRecord: '公開済みレコード',
      openPublishedPost: '公開済みの投稿を開く',
      publishedCannotBeRecalled: 'この投稿は X で公開済みです。このアプリケーションから取り消し・編集・削除することはできません。',
    },
    schedulePanel: {
      title: '予約',
      localLabel: 'ローカル日時',
      utcLabel: '正規化された UTC 時刻',
      timezoneLabel: 'ブラウザのタイムゾーン',
      save: '予約を保存',
      pastError: 'その時刻は過去です。後の時刻を選んでください。',
      requiredError: '日時を選択してください。',
      needsApproval: '予約する前にこのリビジョンを承認してください。',
      currentlyScheduled: '現在の予約',
    },
    rescheduleConfirm: {
      title: '予約変更の確認',
      fromLabel: '現在の予約',
      toLabel: '新しい予約',
      approvalPreserved: 'これは予約日時のみの変更です。承認は維持され、この予約変更は監査イベントとして記録されます。',
      confirm: '予約変更を確定',
      cancel: 'キャンセル',
      pastError: '新しい時刻はすでに過去です。現在より後の時刻を選んでください。',
      timezoneChanged: (zone) => `この画面を開いている間に端末のタイムゾーンが ${zone} に変わりました。保存する前に、下の更新された時刻を確認してください。`,
      reviewUpdatedTimes: '更新された時刻を確認する',
    },
    project: {
      title: 'プロジェクト',
      website: 'ウェブサイト',
      connectXAccount: 'X アカウントを接続',
      connectPinterestAccount: 'Pinterest アカウントを接続',
      connected: '接続済み',
      notConnected: '未接続',
      boards: 'ボード',
      backToHome: 'ホームに戻る',
      loading: '読み込み中…',
      notFound: 'プロジェクトが見つかりません。',
    },
    counter: {
      weighted: 'weighted',
      codepoints: '文字',
    },
    createProject: {
      title: 'プロジェクトを作成',
      nameLabel: 'プロジェクト名',
      websiteLabel: 'ウェブサイト（任意）',
      dailyCapLabel: '1日あたりの公開上限',
      dailyCapHint: 'このプロジェクトが1日に公開できる投稿数の上限です。このフェーズではまだ強制されません。',
      submit: 'プロジェクトを作成',
      cancel: 'キャンセル',
      nameRequired: 'プロジェクト名を入力してください。',
      dailyCapInvalid: '0以上の整数を入力してください。',
      creating: '作成中…',
      createFailed: 'プロジェクトを作成できませんでした。',
    },
    projects: {
      title: 'プロジェクト',
      nameColumn: 'プロジェクト',
      connectionsColumn: '連携',
      automationColumn: '自動化',
      nextPublishColumn: '次回公開予定',
      attentionColumn: '要対応',
      open: '開く',
      noUpcoming: '予定なし',
      noProjects: 'プロジェクトがまだありません',
      noProjectsBody: 'キャンペーンはすべてプロジェクトの中に作成されます。まずプロジェクトを作成してください。',
      loading: '読み込み中…',
      loadFailed: 'プロジェクトを読み込めませんでした。',
      retry: '再試行',
    },
    campaigns: {
      title: 'キャンペーン',
      nameColumn: 'キャンペーン',
      objectiveColumn: '目的',
      variantsColumn: 'バリアント',
      nextPublishColumn: '次回公開予定',
      noObjective: '目的未設定',
      noUpcoming: '予定なし',
      noCampaigns: 'キャンペーンがまだありません',
      noCampaignsBody: 'キャンペーンはプロジェクトの中に作成されます。',
      loading: '読み込み中…',
      loadFailed: 'キャンペーンを読み込めませんでした。',
      retry: '再試行',
      summaryUniform: (count, total, stateLabel) => `${total}件中${count}件が${stateLabel}`,
      summaryPart: (count, stateLabel) => `${stateLabel} ${count}件`,
      summaryJoin: '、',
      summaryReviewRequired: (platforms) => `${platforms} で要レビュー`,
      summaryNoVariants: 'バリアントがまだありません',
      summaryStateLabel: {
        draft: '下書き',
        validated: '検証済み',
        needsReview: '要レビュー',
        policyCheck: 'ポリシー判定中',
        approved: '承認済み',
        scheduled: '予約済み',
        publishing: '公開処理中',
        published: '公開済み',
        failed: '失敗',
        canceled: '取消済み',
        held: '保留',
      },
    },
    activity: {
      title: 'アクティビティ',
      timeColumn: '日時',
      projectColumn: 'プロジェクト',
      originColumn: '発信元',
      actionColumn: '操作',
      targetColumn: '対象',
      resultColumn: '結果',
      noProject: '—',
      loading: '読み込み中…',
      loadFailed: 'アクティビティを読み込めませんでした。',
      retry: '再試行',
      filterOriginLabel: '発信元',
      filterOriginAll: 'すべて',
      noEvents: 'まだ記録されたアクティビティはありません。',
      noEventsBody: 'UI・MCP・スケジューラー・公開処理での操作がここに表示されます。',
      noMatch: 'この条件に一致するアクティビティはありません。',
      noMatchBody: '発信元の条件を変更するか、フィルターを解除してください。',
      resultOk: '成功',
      resultFailed: '失敗',
    },
    aiConnections: {
      title: 'AI接続',
      loading: '読み込み中…',
      loadFailed: 'AI接続を読み込めませんでした。',
      retry: '再試行',
      endpointTitle: 'サーバーエンドポイント',
      endpointBody: 'AIクライアントが接続するアドレスです。パスワードやセッションクッキーではなく、1つの認可グラントに紐づくベアラートークンで認証します。',
      copyEndpoint: 'エンドポイントをコピー',
      copied: 'コピーしました',
      setupTitle: 'AIクライアントへのサーバー追加方法',
      setupBody: 'クライアントのMCP設定で、上記のエンドポイントを指す新しいサーバーを追加し、グラント認可時に発行されたベアラートークンを入力してください。トークンを紛失した場合は復元できません。再度認可して新しいトークンを発行してください。',
      clientsTitle: '認可済みクライアント',
      activeSectionTitle: '有効',
      revokedSectionTitle: '取り消し済み',
      noClientsTitle: 'まだ認可済みのクライアントはありません',
      noClientsBody: '最初のクライアントが接続するまで、このワークスペースにアクセスできるAIクライアントはありません。これは想定内の状態です。',
      noActiveClients: '有効なクライアントはありません。',
      noRevokedClients: '取り消し済みのクライアントはありません。',
      stateActive: '有効',
      stateRevoked: '取り消し済み',
      authorizedAtLabel: '認可日時',
      lastActivityLabel: '最終アクティビティ',
      lastActivityNever: 'まだアクティビティはありません',
      readAccessLabel: '読み取りスコープ',
      writeAccessLabel: '書き込みスコープ',
      writeAuthorityLabel: '書き込み権限',
      noScopes: 'なし',
      noWriteAccess: 'なし — このクライアントによる書き込みはすべて人によるレビューを待ちます',
      capabilityApproval: 'さらなる人によるレビューなしにコンテンツを承認できます',
      capabilityPublish: 'コンテンツを即座に公開できます',
      capabilityAutomation: '自動化モードと一時停止を制御できます',
      revokeConfirmTitle: 'このクライアントを取り消しますか？',
      revokeConfirmBody: (client) => `${client} はこのワークスペースのどのツールも呼び出せなくなります。取り消しても、このクライアントがすでに作成したものは削除も取り消しもされません — 認可を撤回するだけです。`,
      revokeConfirmAction: 'アクセスを取り消す',
      revokeCancelAction: 'キャンセル',
      revoking: '取り消し中…',
      revokeFailed: 'クライアントを取り消せませんでした。もう一度お試しください。',
      scopeReferenceTitle: 'スコープ一覧',
      scopeReferenceBody: '現在どのクライアントが保持しているかに関わらず、各スコープが許可する内容です。',
      scopeDescriptions: {
        projectsRead: 'プロジェクトの詳細とブランドコンテキストを閲覧できます。',
        projectsWrite: 'プロジェクトを作成・編集できます。',
        campaignsRead: '公開状況を閲覧できます。',
        campaignsWrite: 'キャンペーンを作成し、投稿内容を作成・編集できます。',
        mediaWrite: 'メディアをアップロードしてコンテンツに添付できます。',
        scheduleWrite: '予約投稿の作成・取り消しができます。',
        approvalWrite: 'さらなる人によるレビューなしにコンテンツを公開承認できます。',
        publishWrite: 'コンテンツを即座に公開できます。',
        automationWrite: '自動化モードと一時停止を制御できます。',
        activityRead: 'ワークスペースのアクティビティログを閲覧できます。',
      },
    },
    brandContext: {
      title: 'ブランドコンテキスト',
      intro: 'このコンテキストは、認可済みのすべてのAIクライアントがブランドに合った内容を作成・提案できるよう共有されます。プロジェクト・キャンペーン・投稿単位の構造化された指示を入力する場所ではありません。',
      backToProject: 'プロジェクトに戻る',
      productSummaryLabel: '製品・サービスの概要',
      websiteLabel: '公式ウェブサイトURL',
      targetAudienceLabel: 'ターゲット層',
      brandVoiceLabel: 'ブランドのトーン＆マナー',
      aiInstructionsLabel: '自由記述のAI向け指示',
      expressionsToAvoidLabel: '避けるべき表現・約束・主張',
      save: '変更を保存',
      saving: '保存中…',
      saved: '保存しました。',
      saveFailed: '変更を保存できませんでした。もう一度お試しください。',
      loading: '読み込み中…',
      loadFailed: 'ブランドコンテキストを読み込めませんでした。',
      notFound: 'プロジェクトが見つかりません。',
    },
    calendar: {
      title: 'カレンダー',
      viewWeek: '週',
      viewMonth: '月',
      viewList: 'リスト',
      previous: '前へ',
      next: '次へ',
      today: '今日',
      filterProjectLabel: 'プロジェクト',
      filterProjectAll: 'すべてのプロジェクト',
      filterPlatformLabel: 'プラットフォーム',
      filterPlatformAll: 'すべてのプラットフォーム',
      filterStateLabel: 'ステータス',
      filterStateAll: 'すべてのステータス',
      scheduleStateLabel: {
        pending: '予約済み',
        claimed: '処理中',
        done: '完了',
        canceled: 'キャンセル済み',
        failed: '失敗',
      },
      timezoneNotice: (tz) => `時刻はブラウザのタイムゾーン（${tz}）で表示しています。`,
      loading: '読み込み中…',
      loadFailed: 'この期間を読み込めませんでした。フィルターとナビゲーションは引き続き利用できます。',
      retry: '再試行',
      noPostsTitle: '予定されている投稿はありません',
      noPostsBody: 'この期間に予約された投稿はありません。',
      filteredEmptyTitle: 'フィルターに一致する投稿はありません',
      filteredEmptyBody: 'この期間には投稿がありますが、有効なフィルターによってすべて非表示になっています。フィルターを解除すると表示されます。',
      detailTitle: 'エントリ',
      detailClose: '閉じる',
      detailProjectLabel: 'プロジェクト',
      detailCampaignLabel: 'キャンペーン',
      detailPlatformLabel: 'プラットフォーム',
      detailStateLabel: '状態',
      detailScheduleStateLabel: '予約ステータス',
      entryApprovalRequired: 'この投稿は公開前に承認が必要です。',
      newTimeLabel: '新しい日時',
      cancelFailed: '予約をキャンセルできませんでした。もう一度お試しください。',
      rescheduleFailed: 'この予約変更を保存できませんでした。もう一度お試しいただくか、キャンセルしてください。',
    },
  },
};
